// pipe: frontend wave (F6) — storefront Catalog container. Wires the anonymous
// public browse (`GET /api/store/listings`, bounded pages) to the pure
// CatalogView. All the fetch/state/effect plumbing lives HERE, never in
// catalog-view.tsx (the Vitest-tested seam).
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import type { StorefrontRecord } from '../services/storefront-api';
import { useStorefrontApi } from '../services/storefront-api-provider';
import { CatalogView } from './catalog-view';

/** One page of the public catalog (the server clamps to its hard max anyway). */
const PAGE_SIZE = 48;

/** The server's hard catalog page cap (store.rs `BROWSE_PAGE_MAX` — a requested
 *  `limit` above this is silently clamped, so a single-request revalidate can
 *  only be trusted up to it). */
const SERVER_PAGE_MAX = 200;

// Session page cache: catalog → product → back remounts this container, so the
// already-loaded pages are kept module-level and restored instantly (no loading
// blank), then page 0 is silently revalidated. Only ever holds the pages the
// user actually viewed — never a prefetch.
let pageCache: { rows: StorefrontRecord[]; mayHaveMore: boolean } | null = null;

export function Catalog() {
  const api = useStorefrontApi();
  const navigate = useNavigate();
  const [rows, setRows] = useState<StorefrontRecord[]>(
    () => pageCache?.rows ?? []
  );
  const [loading, setLoading] = useState(() => pageCache === null);
  const [error, setError] = useState<string | null>(null);
  // Whether the last fetched page was full — if so, more rows may exist.
  const [mayHaveMore, setMayHaveMore] = useState(
    () => pageCache?.mayHaveMore ?? false
  );

  // Latest-wins: a slower stale response must never overwrite a newer load's
  // rows (covers the silent revalidate racing a user-driven Load more).
  const loadSeq = useRef(0);
  // The rows currently on screen, readable inside async catch handlers (a
  // stale closure must not decide whether a usable grid is rendered).
  const rowsRef = useRef(rows);
  rowsRef.current = rows;
  const loadPage = useCallback(
    (offset: number, opts?: { silent?: boolean }) => {
      const seq = ++loadSeq.current;
      // `silent` revalidates in the background (the cache-restore path)
      // without blanking the already-rendered cards behind the loading state.
      if (!opts?.silent) setLoading(true);
      setError(null);
      api
        .browseListings(PAGE_SIZE, offset)
        .then(page => {
          if (loadSeq.current !== seq) return;
          setRows(prev => (offset === 0 ? page : [...prev, ...page]));
          setMayHaveMore(page.length === PAGE_SIZE);
        })
        .catch((e: unknown) => {
          if (loadSeq.current !== seq) return;
          // A FAILED silent revalidate with rows already rendered must never
          // rip away a usable grid behind the full-screen error panel — log
          // and keep the cards. Non-silent failures render exactly as before.
          if (opts?.silent && rowsRef.current.length > 0) {
            console.error('storefront catalog: silent revalidate failed', e);
            return;
          }
          setError(e instanceof Error ? e.message : String(e));
        })
        .finally(() => {
          // Cleared only when the settling load is still the latest — silent
          // included, else a superseded initial load strands the loading state.
          if (loadSeq.current === seq) setLoading(false);
        });
    },
    [api]
  );

  // Silent revalidate of the FULL cached extent after a multi-page back-nav
  // restore — a plain page-0 revalidate would truncate the restored list to
  // PAGE_SIZE. One request when the cached count fits under the server clamp
  // (`SERVER_PAGE_MAX`); otherwise the cached number of pages sequentially,
  // replacing wholesale only when ALL succeed (any failure keeps the cached
  // rows — still silent). Either way offset paging (`rows.length`) and
  // `mayHaveMore` end consistent with whatever rows are displayed.
  const revalidateCachedExtent = useCallback(
    (cachedCount: number) => {
      const seq = ++loadSeq.current;
      setError(null);
      const run = async (): Promise<{
        rows: StorefrontRecord[];
        mayHaveMore: boolean;
      }> => {
        if (cachedCount <= SERVER_PAGE_MAX) {
          const page = await api.browseListings(cachedCount, 0);
          return { rows: page, mayHaveMore: page.length === cachedCount };
        }
        const pages = Math.ceil(cachedCount / PAGE_SIZE);
        const all: StorefrontRecord[] = [];
        let lastLen = 0;
        for (let i = 0; i < pages; i++) {
          const page = await api.browseListings(PAGE_SIZE, i * PAGE_SIZE);
          all.push(...page);
          lastLen = page.length;
          if (page.length < PAGE_SIZE) break; // catalog shrank — nothing further
        }
        return { rows: all, mayHaveMore: lastLen === PAGE_SIZE };
      };
      run()
        .then(fresh => {
          if (loadSeq.current !== seq) return;
          setRows(fresh.rows);
          setMayHaveMore(fresh.mayHaveMore);
        })
        .catch((e: unknown) => {
          if (loadSeq.current !== seq) return;
          // Silent by contract: the cached rows stay rendered.
          console.error('storefront catalog: silent revalidate failed', e);
        });
    },
    [api]
  );

  useEffect(() => {
    // Cache hit: the cached pages already render — revalidate silently. A
    // single restored page revalidates via the page-0 load (same offset-0
    // replace semantics as before); a multi-page restore revalidates its full
    // cached extent so the fresh result never collapses the list.
    if (pageCache && pageCache.rows.length > PAGE_SIZE) {
      revalidateCachedExtent(pageCache.rows.length);
    } else {
      loadPage(0, pageCache ? { silent: true } : undefined);
    }
  }, [loadPage, revalidateCachedExtent]);

  // Keep the session cache in step with what settled on screen: a clean settle
  // ALWAYS syncs the cache — including an EMPTY result (else stale delisted
  // rows resurrect from the cache forever). An errored or still-loading state
  // never overwrites it.
  useEffect(() => {
    if (!loading && error === null) {
      pageCache = { rows, mayHaveMore };
    }
  }, [rows, mayHaveMore, error, loading]);

  const onOpen = useCallback(
    (id: string) => navigate(`/products/${id}`),
    [navigate]
  );

  const onLoadMore = useCallback(
    () => loadPage(rows.length),
    [loadPage, rows.length]
  );

  return (
    <CatalogView
      rows={rows}
      loading={loading}
      error={error}
      mayHaveMore={mayHaveMore}
      onOpen={onOpen}
      onLoadMore={onLoadMore}
    />
  );
}
